// A refill sheet in the Order Chat transcript (group-home roster refills, PR-E; O170).
//
// One turn per sheet, polled until it is read and staged (lib/refill-sheet's poller, run
// by the page). It shows where the sheet is (queued → reading → matching → done), which
// model read it, and then every line IN SHEET ORDER, one of three ways:
//   drafted          the resident (name + DOB), the chart med as matched, the strength
//                    rule, whether the facility's Rx# went to the pharmacy, and a link to
//                    the chart in Elation (where the unsigned draft waits)
//   needs review     why, the sheet's own words for the line ("From sheet, line N", with
//                    how legible each field was), and the line's card — rendered by the
//                    page's OWN batch card renderer (`renderCard`), so open / pick / edit /
//                    confirm / cancel are the existing controls — or, for a resident the
//                    home's tags did not find, the search across all patients (scope (b))
//   not drafted      why, in plain words
// plus the page viewer, the printable summary (F7) and "Discard sheet" (F14), behind the
// app's own confirm dialog.
//
// O180: under "From sheet, line N" the row's own handwriting, cut from the page the
// model read (./line-crop), when the read placed the row; a click opens the viewer on
// that page with the row highlighted. And every control on the sheet's cards answers IN
// PLACE (the page's sheet doors): the line shows the card's pending state and any
// refusal itself, the server's sentence is spoken in the sheet's live region, and focus
// comes back to the line — the clicked control if it is still there, else the line's
// next control, else the line.
import React, { useEffect, useId, useReducer, useRef, useState } from 'react';
import { ccmApi } from '../../lib/api.js';
import { confirmDialog } from '../confirm-dialog.jsx';
import { SheetViewer } from './sheet-viewer.jsx';
import { LineCrop } from './line-crop.jsx';
import { SheetPrintPortal } from './sheet-print.jsx';
import {
  PROGRESS_STEPS, progressStepOf, readByText, errorText, reasonText, statusLabel, legibilityLabel,
  residentLabel, sheetMedText, strengthRuleText, elationChartUrl, summaryCounts, formatDob,
  searchReducer, runLineSearch, runLinePick, discardSheetFlow,
  duplicateOfText, ACTION_LABELS, THREAD_FALLBACK_TEXT, lineActionReducer, runLineAction,
  draftedFacts, draftedStrengthFacts, DRAFTED_UNKNOWN, candidateActions, discardSheet,
  lineCrop, focusLineAfterAnswer, focusAfterConfirmAll, inputCount, lineErrorShown,
} from '../../lib/refill-sheet.js';
import { allOpened, confirmableSessionIds, openedCount, OPEN_EACH_CARD_HINT } from '../../lib/order-batch-opened.js';
import '../../styles/refill-sheet.css';

export function SheetProgress({ step }) {
  const at = PROGRESS_STEPS.findIndex((s) => s.key === step);
  return (
    <ol className="rs-progress" aria-label="Progress">
      {PROGRESS_STEPS.map((s, i) => {
        const state = step === 'failed' ? (i === 0 ? 'done' : 'todo') : (i < at ? 'done' : i === at ? 'current' : 'todo');
        return (
          <li key={s.key} className={`rs-step rs-step-${state}`} aria-current={state === 'current' ? 'step' : undefined}>
            {s.label}
          </li>
        );
      })}
    </ol>
  );
}

const LEGIBILITY_FIELDS = [
  ['name', 'Resident', 'residentName'],
  ['drug', 'Drug', 'drugAsWritten'],
  ['strength', 'Strength', 'strengthAsWritten'],
  ['rx', 'Pharmacy Rx#', 'rxNumber'],
];

// The sheet's own words for one line, field by field, with how sure the reader was — and,
// when the read placed the row on its page (O180), the row's own handwriting.
export function FromSheetBlock({ line, onView, rosterId = null, crop = null }) {
  const leg = line.legibility || {};
  return (
    <div className="rs-from-sheet">
      <div className="rs-from-sheet-head">
        <span>From sheet, line {line.line}</span>
        {onView && (
          <button type="button" className="rs-link" onClick={onView}>View on the sheet</button>
        )}
      </div>
      {crop && rosterId && onView && <LineCrop rosterId={rosterId} line={line.line} crop={crop} onOpen={onView} />}
      <dl>
        {LEGIBILITY_FIELDS.map(([key, label, prop]) => {
          const value = line[prop];
          const l = leg[key];
          return (
            <div key={key} className="rs-from-sheet-row">
              <dt>{label}</dt>
              <dd>
                <span>{value || '—'}</span>
                {l && (
                  <span className={`rs-leg rs-leg-${l}`}>{legibilityLabel(l)}</span>
                )}
              </dd>
            </div>
          );
        })}
        {line.drugGenericGuess && String(line.drugGenericGuess).toLowerCase() !== String(line.drugAsWritten || '').toLowerCase() && (
          <div className="rs-from-sheet-row">
            <dt>Read as</dt>
            <dd><span>{line.drugGenericGuess}</span></dd>
          </div>
        )}
        {line.notes && (
          <div className="rs-from-sheet-row">
            <dt>Note</dt>
            <dd><span>{line.notes}</span></dd>
          </div>
        )}
      </dl>
    </div>
  );
}

// Scope (b): the provider's explicit search across every patient of the practice for a
// resident the home's tags did not find. Every hit is a pick — never matched for them —
// and says whether that chart is tagged for this home.
export function LineSearch({ rosterId, line, residentName, onPicked, client = ccmApi, initial = null }) {
  const [state, dispatch] = useReducer(searchReducer, initial || undefined, (s) => s || searchReducer(undefined, { type: 'reset' }));
  const busy = state.phase === 'searching' || state.phase === 'picking';
  async function search() {
    dispatch({ type: 'search' });
    dispatch(await runLineSearch(client, rosterId, line));
  }
  async function pick(patientId) {
    dispatch({ type: 'pick', patientId });
    const r = await runLinePick(client, rosterId, line, patientId);
    dispatch(r);
    if (r.type === 'picked' && onPicked) onPicked(r.sessionId);
  }
  return (
    <div className="rs-search">
      {(state.phase === 'idle' || state.phase === 'searching') && (
        <button type="button" className="btn sm" data-action="search-all" disabled={busy} onClick={search}>
          {state.phase === 'searching' ? 'Searching…' : `Search all patients for “${residentName || 'this name'}”`}
        </button>
      )}
      {(state.phase === 'results' || state.phase === 'picking') && (
        state.candidates.length === 0 ? (
          <div className="rs-muted" role="status">
            No patient in the practice has that name. Check the sheet, or order it in chat by name.
            {' '}<button type="button" className="rs-link" onClick={search}>Search again</button>
          </div>
        ) : (
          <ul className="rs-candidates" aria-label={`Patients named like ${residentName || 'this line'}`}>
            {candidateActions(state.candidates, pick).map((c) => (
              <li key={c.id}>
                <span className="rs-candidate-name">{c.name}</span>
                {c.dob && <span className="rs-muted"> · DOB {c.dob}</span>}
                <span className={`rs-tag ${c.inFacility ? 'rs-tag-in' : 'rs-tag-out'}`}>{c.tag}</span>
                <button
                  type="button"
                  className="btn sm"
                  disabled={busy}
                  onClick={c.run}
                  aria-label={c.ariaLabel}
                >
                  {state.phase === 'picking' && state.pickedId === c.id ? 'Setting…' : 'Use this chart'}
                </button>
              </li>
            ))}
          </ul>
        )
      )}
      {state.phase === 'picked' && <div className="rs-muted" role="status">Chart set — the card is below once it loads.</div>}
      {state.error && <div className="rs-error" role="alert">{state.error}</div>}
    </div>
  );
}

// A card's open question when it has no card to carry it (a resident pick: the server
// draws no order card until the patient is known) — the same options the chat's chips
// would show, answered through the same pick door, naming THIS card's conversation.
export function CardQuestion({ view, onPick, disabled = false }) {
  const q = view && view.question;
  if (!q || !Array.isArray(q.options)) return null;
  return (
    <div className="rs-question" role="group" aria-label={q.prompt || q.label || 'Question'}>
      {q.prompt && <div className="rs-question-prompt">{q.prompt}</div>}
      <div className="rs-question-options">
        {q.options.map((o, i) => (
          <button key={`${o.optionId}-${i}`} type="button" className="orderchat-chip" disabled={disabled}
            onClick={() => onPick && onPick(view, o)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// "Try again" (a line the chart read or the staging could not finish) and "Draft anyway"
// (a duplicate, as a card the provider then confirms — never an automatic draft) — the
// outcome's own `actions` (#747 r2 D4), one button each; the sheet re-reads itself after.
export function LineAction({ rosterId, line, kind, onDone, client = ccmApi, initial = null }) {
  const [state, dispatch] = useReducer(lineActionReducer, initial || undefined, (s) => s || lineActionReducer(undefined, {}));
  async function run() {
    dispatch({ type: 'start' });
    const r = await runLineAction(client, kind, rosterId, line);
    dispatch(r);
    if (r.type === 'done' && onDone) onDone(r.result);
  }
  const hint = kind === 'draft_anyway' ? 'opens a card for you to confirm' : 'reads the chart again';
  return (
    <div className="rs-line-action">
      {state.phase !== 'done' && (
        <button type="button" className="btn sm" data-action={`line-${kind}`} disabled={state.phase === 'working'} onClick={run}
          aria-label={`${ACTION_LABELS[kind]} for line ${line} — ${hint}`}>
          {state.phase === 'working' ? 'Working…' : ACTION_LABELS[kind]}
        </button>
      )}
      {state.phase === 'done' && <span className="rs-muted" role="status">Done — the line is updated below once it reloads.</span>}
      {state.error && <div className="rs-error" role="alert">{state.error}</div>}
    </div>
  );
}

// Which control on a line the provider pressed last — focus goes back to it after an
// in-place answer when it is still there (focusLineAfterAnswer).
function noteClicked(ref, e) {
  if (!ref) return;
  const t = e && e.target;
  ref.current = t && typeof t.closest === 'function' ? t.closest('button, a[href]') : null;
}

function LineRow({ turn, line, views, renderCard, onPickOption, onView, onRefresh, client, pages = null, clickedRef = null }) {
  const o = line.outcome || { status: 'pending' };
  const headId = `rs-line-${turn.rosterId}-${line.line}`;
  const resident = residentLabel(o.patient);
  const rule = strengthRuleText(o, line);
  // A DRAFTED line is what was filed (#747 r2c `outcome.filed`; D1) — its own patient,
  // med, strength, Rx#, thread and draft — never staging's guess or the sheet's words.
  const f = draftedFacts(o);
  const filedRule = strengthRuleText(draftedStrengthFacts(o, line), line);
  const chartUrl = f.patient && f.patient.elationId ? elationChartUrl(f.patient.elationId) : null;
  const sid = o.sessionId != null ? String(o.sessionId) : null;
  const hasEntry = !!(sid && Array.isArray(turn.batch) && turn.batch.some((e) => e && String(e.sessionId) === sid));
  const view = sid ? ((views && views[sid]) || (Array.isArray(turn.views) ? turn.views.find((v) => v && String(v.sessionId) === sid) : null)) : null;
  // O180: this card's in-place answer — in flight, or refused (said here, on the line).
  const answer = (sid && turn.inPlace && turn.inPlace[sid]) || {};
  const pending = !!answer.pending;
  const crop = lineCrop(line, pages);
  return (
    <li className={`rs-line rs-line-${o.status}`} aria-labelledby={headId} data-line={line.line} tabIndex={-1}
      aria-busy={pending || undefined} onClickCapture={(e) => noteClicked(clickedRef, e)}>
      <div className="rs-line-head" id={headId}>
        <span className="rs-line-no">Line {line.line}</span>
        <span className={`rs-badge rs-badge-${o.status}`}>{statusLabel(o.status)}</span>
        <span className="rs-line-sheet">{line.residentName || '(name not readable)'} — {sheetMedText(line) || '(drug not readable)'}</span>
      </div>

      {o.status === 'drafted' && (
        <div className="rs-line-body">
          <div className="rs-facts">
            <div data-role="drafted-resident"><span className="rs-fact-label">Resident</span> {residentLabel(f.patient) || DRAFTED_UNKNOWN.screenResident}</div>
            <div data-role="drafted-med"><span className="rs-fact-label">Drafted</span> {f.medName
              ? `${f.medName}${f.via === 'card' ? ' (from your card)' : ''}`
              : (f.via === 'card' ? DRAFTED_UNKNOWN.screenMedCard : DRAFTED_UNKNOWN.screenMed)}</div>
            {filedRule && <div><span className="rs-fact-label">Strength</span> {filedRule}</div>}
            <div><span className="rs-fact-label">Rx# to pharmacy</span> {f.rxToPharmacy ? `Yes${line.rxNumber ? ` (${line.rxNumber})` : ''}` : 'No'}</div>
          </div>
          {f.threadFallback && <div className="rs-warn" role="note">{THREAD_FALLBACK_TEXT}</div>}
          {chartUrl && (
            <a className="rs-link" href={chartUrl} target="_blank" rel="noreferrer">
              Open the chart in Elation{f.draftId ? ` (unsigned draft #${f.draftId})` : ''}
            </a>
          )}
        </div>
      )}

      {o.status === 'needs_attention' && (
        <div className="rs-line-body">
          <div className="rs-reason">{reasonText('needs_attention', o.reason)}</div>
          {duplicateOfText(o, turn.rosterId) && <div className="rs-muted">{duplicateOfText(o, turn.rosterId)}</div>}
          {resident && <div className="rs-muted">Matched: {resident}</div>}
          {rule && <div className="rs-muted">{rule}</div>}
          <FromSheetBlock line={line} onView={onView} rosterId={turn.rosterId} crop={crop} />
          {sid && !hasEntry && view && view.question && (
            <CardQuestion view={view} onPick={onPickOption} disabled={pending} />
          )}
          {sid && hasEntry && renderCard && (
            <div className={`rs-line-card${pending ? ' rs-line-card-pending' : ''}`} aria-busy={pending || undefined}>
              {renderCard(sid)}
            </div>
          )}
          {sid && !hasEntry && !(view && view.question) && o.cardState === 'processing' && (
            <div className="rs-muted" role="status">This line's card is being filed right now.</div>
          )}
          {!sid && Array.isArray(o.actions) && o.actions.includes('search_all') && (
            <LineSearch rosterId={turn.rosterId} line={line.line} residentName={line.residentName} client={client}
              onPicked={() => onRefresh && onRefresh()} />
          )}
        </div>
      )}

      {o.status === 'not_drafted' && (
        <div className="rs-line-body">
          <div className="rs-reason">{reasonText('not_drafted', o.reason)}</div>
          {duplicateOfText(o, turn.rosterId) && <div className="rs-muted">{duplicateOfText(o, turn.rosterId)}</div>}
          {resident && <div className="rs-muted">Matched: {resident}</div>}
          {rule && <div className="rs-muted">{rule}</div>}
          {(Array.isArray(o.actions) ? o.actions : []).filter((a) => a === 'retry' || a === 'draft_anyway').map((a) => (
            <LineAction key={a} rosterId={turn.rosterId} line={line.line} kind={a} client={client}
              onDone={() => onRefresh && onRefresh()} />
          ))}
        </div>
      )}

      {o.status === 'pending' && <div className="rs-line-body rs-muted">Working on it…</div>}

      {/* D1: a refusal is said on its line whatever the line's status now is (its own
          re-read may have made it drafted or not drafted), and stops once the line has
          moved on from the status it was said under. */}
      {lineErrorShown(answer, o.status) && <div className="rs-error" role="alert" data-role="line-error">{answer.error}</div>}
    </li>
  );
}

// Does the sheet still hold a card the provider has to answer?
function cardsOpen(turn) {
  return (Array.isArray(turn.sessions) ? turn.sessions : []).some((x) => x && !['completed', 'cancelled', 'expired'].includes(x.state));
}

export function RefillSheetTurn({
  turn, views, renderCard, onPickOption, onConfirmAll, onRefresh, onUploadAgain,
  client = ccmApi, confirm = confirmDialog, directSendOn = false,
}) {
  const job = turn.sheet || {};
  const step = progressStepOf(job);
  const counts = summaryCounts(job);
  const [viewer, setViewer] = useState(null); // { line, crop } | null
  const sectionRef = useRef(null);
  const confirmAllRef = useRef(null);
  const clickedRef = useRef(null);
  const [printing, setPrinting] = useState(false);
  const [notice, setNotice] = useState(null);
  const titleId = `rs-title-${useId()}`;
  const hintId = `rs-hint-${useId()}`;
  const read = readByText(job);
  const lines = Array.isArray(job.lines) ? job.lines : [];
  const done = step === 'done';
  const discarded = !!(job.staging && job.staging.discardedAt);
  const pages = Number(job.pageCount) || 1;

  // A card answered or confirmed moves its roster row; the line's outcome is the
  // server's to say, so the sheet re-reads itself when any of its cards' states move.
  const sig = (Array.isArray(turn.sessions) ? turn.sessions : []).map((s) => `${s.sessionId}:${s.state}`).join('|');
  const lastSig = useRef(sig);
  useEffect(() => {
    if (sig === lastSig.current) return undefined;
    const first = lastSig.current === '';   // the read that brought the cards needs no re-read
    lastSig.current = sig;
    if (first || !done || !onRefresh) return undefined;
    const t = setTimeout(() => onRefresh(), 400);
    return () => clearTimeout(t);
  }, [sig, done]); // eslint-disable-line react-hooks/exhaustive-deps

  const cardIds = confirmableSessionIds(turn);
  const opened = openedCount(turn);
  const reviewed = allOpened(turn);
  const all = (turn.inPlace && turn.inPlace['*']) || {};

  // O180: after an in-place answer has landed (and the sheet has re-read itself), focus
  // goes back to where it was given — a render effect, so it runs on the DOM the answer
  // left — under I-FOCUS: only if the provider has not tapped or typed anywhere on the
  // page since the answer started (the page-wide counter, snapshotted at the start).
  const focusReq = turn.focusReq || null;
  useEffect(() => {
    if (!focusReq || typeof document === 'undefined') return;
    const gate = { inputAt: focusReq.inputAt, inputNow: inputCount() };
    if (focusReq.key === '*') {
      focusAfterConfirmAll(document, { section: sectionRef.current, button: confirmAllRef.current, ...gate });
      return;
    }
    focusLineAfterAnswer(document, { rosterId: turn.rosterId, line: focusReq.line, clicked: clickedRef.current, ...gate });
  }, [focusReq && focusReq.seq]); // eslint-disable-line react-hooks/exhaustive-deps

  async function discard() {
    const r = await discardSheet({ client, confirm, turn });
    if (r.discarded) {
      setNotice(`Sheet discarded. ${r.cardsEnded} card${r.cardsEnded === 1 ? '' : 's'} cancelled; drafts already in Elation stay there.`);
      if (onRefresh) onRefresh();
    } else if (r.error) {
      setNotice(r.error);
    }
  }

  return (
    <section ref={sectionRef} tabIndex={-1} className="rs-sheet orderchat-bubble-in" aria-labelledby={titleId} data-roster-id={turn.rosterId}>
      <header className="rs-sheet-head">
        <div>
          <h4 id={titleId} className="rs-sheet-title">
            Refill sheet{job.facilityName ? ` — ${job.facilityName}` : ''}
          </h4>
          <div className="rs-sheet-sub">
            {job.createdAt ? new Date(job.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Just now'}
            {job.pageCount ? ` · ${job.pageCount} page${job.pageCount === 1 ? '' : 's'}` : ''}
            {job.lineCount ? ` · ${job.lineCount} lines` : ''}
            {read ? ` · ${read}` : ''}
          </div>
        </div>
      </header>

      <SheetProgress step={step} />
      <div className="rs-visually-hidden" role="status" aria-live="polite">
        {step === 'failed' ? 'Reading the sheet failed.' : (PROGRESS_STEPS.find((s) => s.key === step) || {}).label}
      </div>
      {/* O180: what an in-place answer did, in the server's own words. */}
      <div className="rs-visually-hidden" role="status" aria-live="polite" data-role="sheet-announce">
        {turn.announce ? turn.announce.text : ''}
      </div>

      {step === 'failed' && (
        <div className="rs-failed" role="alert">
          <div>{errorText(job.errorCode)}</div>
          {onUploadAgain && (
            <button type="button" className="btn sm primary" onClick={onUploadAgain}>Upload again</button>
          )}
        </div>
      )}
      {done && job.staging && job.staging.status === 'failed' && (
        <div className="rs-failed" role="alert">
          The sheet was read, but its lines couldn't all be matched and drafted. Lines below say what happened to each.
        </div>
      )}
      {turn.pollStopped === 'timeout' && !done && step !== 'failed' && (
        <div className="rs-muted" role="status">
          This is taking longer than usual.{' '}
          <button type="button" className="rs-link" onClick={() => onRefresh && onRefresh()}>Check again</button>
        </div>
      )}
      {turn.pollStopped === 'gone' && (
        <div className="rs-failed" role="alert">This sheet is no longer available.</div>
      )}
      {turn.pollStopped === 'errors' && !done && (
        <div className="rs-failed" role="alert">
          The sheet's status couldn't be loaded.{' '}
          <button type="button" className="rs-link" onClick={() => onRefresh && onRefresh()}>Try again</button>
        </div>
      )}

      {done && (
        <div className="rs-counts" data-role="sheet-counts">
          <span className="rs-count rs-count-drafted"><strong>{counts.drafted}</strong> drafted</span>
          <span className="rs-count rs-count-attention"><strong>{counts.needsAttention}</strong> need{counts.needsAttention === 1 ? 's' : ''} your review</span>
          <span className="rs-count rs-count-not"><strong>{counts.notDrafted}</strong> not drafted</span>
          {counts.pending > 0 && <span className="rs-count"><strong>{counts.pending}</strong> still working</span>}
        </div>
      )}

      {(done || step === 'staging' || step === 'reading') && (
        <div className="rs-actions">
          {job.status === 'done' && (
            <button type="button" className="btn ghost sm" onClick={() => setViewer({ line: null })}>View sheet</button>
          )}
          {done && (
            <button type="button" className="btn ghost sm" data-action="print-sheet"
              onClick={() => (printing ? window.print() : setPrinting(true))}>
              Print summary
            </button>
          )}
          {done && !discarded && (
            <button type="button" className="btn ghost sm" data-action="discard-sheet" onClick={discard}>Discard sheet</button>
          )}
          {done && onRefresh && <button type="button" className="btn ghost sm" onClick={() => onRefresh()}>Refresh</button>}
        </div>
      )}
      {discarded && <div className="rs-muted" role="status">This sheet was discarded. Drafts already in Elation stay there, unsigned.</div>}
      {notice && <div className="rs-muted" role="status">{notice}</div>}
      {/* #757 (O178): the provider's "Create drafts directly" is for dictated orders. A
          sheet's cards never take it (the server stages them with directSend: null) — its
          confident lines are the sheet's own auto-draft (O170) — so this says so where the
          provider would otherwise expect it. */}
      {directSendOn && done && !discarded && cardsOpen(turn) && (
        <div className="rs-muted" role="note" data-role="direct-send-note">
          “Create drafts directly” doesn't apply to a sheet: its cards always wait for your Confirm.
        </div>
      )}

      {lines.length > 0 && (
        <ol className="rs-lines" aria-label="Lines on the sheet">
          {lines.map((l) => (
            <LineRow
              key={l.line}
              turn={turn}
              line={l}
              views={views}
              renderCard={renderCard}
              onPickOption={onPickOption}
              onView={job.status === 'done' ? () => setViewer({ line: l.line, crop: lineCrop(l, pages) }) : null}
              onRefresh={onRefresh}
              client={client}
              pages={pages}
              clickedRef={clickedRef}
            />
          ))}
        </ol>
      )}

      {done && !discarded && cardIds.length > 1 && onConfirmAll && (
        <div className="rs-confirm-all">
          {!reviewed && <span id={hintId} className="rs-muted">{OPEN_EACH_CARD_HINT}</span>}
          <button
            ref={confirmAllRef}
            type="button"
            className="btn sm"
            onClick={onConfirmAll}
            disabled={!reviewed || !!all.pending}
            aria-describedby={reviewed ? undefined : hintId}
          >
            {all.pending ? 'Confirming…' : `Confirm all reviewed cards (${opened} of ${cardIds.length} opened)`}
          </button>
        </div>
      )}
      {all.error && <div className="rs-error" role="alert" data-role="confirm-all-error">{all.error}</div>}
      {!all.error && all.said && <div className="rs-muted" data-role="confirm-all-result">{all.said}</div>}

      {viewer && (
        <SheetViewer
          rosterId={turn.rosterId}
          pageCount={pages}
          line={viewer.line}
          page={viewer.crop ? viewer.crop.page : 1}
          highlight={viewer.crop ? viewer.crop.band : null}
          facilityName={job.facilityName}
          onClose={() => setViewer(null)}
        />
      )}
      {printing && <SheetPrintPortal job={job} onDone={() => setPrinting(false)} />}
    </section>
  );
}
